import * as React from "react";
import { Instagram, Mail, MapPin, Phone, Twitter, ArrowRight, ExternalLink } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const socialPosts = [{
  id: "post-1",
  platform: "Instagram",
  handle: "@ivorylens_studio",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/wedding_veil_detail.png",
  text: "The delicate dance of light and lace. Capturing the quiet moments that define your morning preparations.",
  date: "1 day ago",
  link: "https://instagram.com"
}, {
  id: "post-2",
  platform: "Twitter",
  handle: "@ivorylens_studio",
  image: "",
  text: "New Journal Entry: Why natural lighting is the secret ingredient to timeless wedding portraits. Read our latest guide.",
  date: "4 days ago",
  link: "https://twitter.com"
}, {
  id: "post-3",
  platform: "Instagram",
  handle: "@ivorylens_studio",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/couple_sunset_embrace.png",
  text: "Golden hour at the estate. There is something truly magical about the way the sun kisses the horizon during a first dance.",
  date: "1 week ago",
  link: "https://instagram.com"
}, {
  id: "post-4",
  platform: "Instagram",
  handle: "@ivorylens_studio",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675594/img/reception_table_decor.png",
  text: "It is all in the details. We believe the small touches are just as important as the big 'I do' moments.",
  date: "2 weeks ago",
  link: "https://instagram.com"
}];
export default function StartYourJourney() {
  return <section data-section-id="233620" id="studio-information" className="bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {}
          <div className="flex flex-col space-y-10">
            <div className="space-y-6">
              <h2 className="font-default text-7xl md:text-8xl lg:text-9xl font-light tracking-tighter text-foreground leading-none">
                Connect.
              </h2>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-md font-default italic">
                Your story deserves to be told with elegance. Reach out to discuss your vision for a timeless collection.
              </p>
            </div>

            <div className="flex flex-col space-y-6 pt-8 border-t border-border/50">
              <div className="flex items-start space-x-4 group">
                <div className="bg-accent/50 p-3 rounded-md text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-500">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-default font-medium text-lg text-foreground">The Studio</h3>
                  <p className="text-muted-foreground font-sans font-light">Charleston, South Carolina</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="bg-accent/50 p-3 rounded-md text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-500">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-default font-medium text-lg text-foreground">Inquiries</h3>
                  <p className="text-muted-foreground font-sans font-light">+1 (843) 555-0192</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="bg-accent/50 p-3 rounded-md text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-500">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-default font-medium text-lg text-foreground">Direct Email</h3>
                  <p className="text-muted-foreground font-sans font-light">hello@ivorylens.com</p>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Button size="lg" className="w-full sm:w-auto group bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-sans font-light tracking-wide" asChild>
                <Link to="/InquiryPage">
                  Begin Your Experience
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="relative">
            {}
            <div className="absolute -inset-4 bg-secondary/40 rounded-3xl blur-3xl -z-10"></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {socialPosts.map((post, i) => <div data-index={i} key={post.id} className={`flex flex-col ${i % 2 !== 0 ? 'sm:mt-12' : ''}`}>
                  <Card className="h-full border-border/40 shadow-sm hover:shadow-lg transition-all duration-700 bg-card/80 backdrop-blur-md group rounded-md">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                      <div className="flex items-center space-x-2">
                        {post.platform === "Instagram" ? <Instagram className="w-5 h-5 text-muted-foreground/60" /> : <Twitter className="w-5 h-5 text-muted-foreground/60" />}
                        <span className="font-sans text-xs font-light tracking-widest text-foreground uppercase">
                          {post.handle}
                        </span>
                      </div>
                      <Link to={post.link} newTab className="text-muted-foreground hover:text-primary transition-colors">
                        <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="sr-only">View on {post.platform}</span>
                      </Link>
                    </CardHeader>
                    
                    {post.image && <div className="relative w-full aspect-square overflow-hidden">
                        <img src={post.image} alt={`Ivory Lens photography on ${post.platform}`} className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-1000 ease-out" />
                      </div>}
                    
                    <CardContent className={`pt-4 ${!post.image ? 'flex-grow flex items-center' : ''}`}>
                      <p className="text-sm text-card-foreground font-sans font-light leading-relaxed line-clamp-4">
                        {post.text}
                      </p>
                    </CardContent>
                    
                    <CardFooter className="pt-0 mt-auto">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] font-sans">
                        {post.date}
                      </span>
                    </CardFooter>
                  </Card>
                </div>)}
            </div>
          </div>

        </div>
      </div>
    </section>;
}